import { LocalStorageKeys } from "src/enums";
import { ShipmentJobKind } from "src/types/api/shipments/shipmentJob";

export interface InFlightShipmentJob {
  jobId: string;
  kind: ShipmentJobKind;
  startedAt: string;
  // For edit jobs we keep the shipmentId so we can offer "return to shipment"
  // from the resume banner even when the job is still in flight.
  shipmentIdHint?: string;
}

const KEY = LocalStorageKeys.SHIPMENT_JOBS_IN_FLIGHT;

// Drop any persisted job older than this. Backend TTL is 24h, but we also
// stop polling locally at the 30 minute mark — anything older than that is
// stale (e.g. user closed the tab and the worker died) and should not block
// fresh submissions.
const STALE_AFTER_MS = 30 * 60 * 1000;

function safeRead(): InFlightShipmentJob[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as InFlightShipmentJob[]) : [];
  } catch {
    // Corrupt entry — drop it rather than crash the page.
    return [];
  }
}

function safeWrite(items: InFlightShipmentJob[]): void {
  try {
    if (items.length === 0) localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, JSON.stringify(items));
  } catch {
    // Quota / private mode — silently no-op. The in-memory job state still
    // works for the current tab; we just lose refresh-resume.
  }
}

export const shipmentJobStorage = {
  list(): InFlightShipmentJob[] {
    const now = Date.now();
    const all = safeRead();
    const fresh = all.filter((j) => {
      const ts = Date.parse(j.startedAt);
      return Number.isFinite(ts) && now - ts < STALE_AFTER_MS;
    });
    // Persist the pruned list so stale entries don't accumulate. Read once
    // to avoid racing with concurrent writes from another tab.
    if (fresh.length !== all.length) safeWrite(fresh);
    return fresh;
  },

  add(job: InFlightShipmentJob): void {
    const existing = safeRead().filter((j) => j.jobId !== job.jobId);
    safeWrite([job, ...existing]);
  },

  remove(jobId: string): void {
    safeWrite(safeRead().filter((j) => j.jobId !== jobId));
  },

  clear(): void {
    safeWrite([]);
  }
};
